/**
 * Owns the single shared AudioContext and its two buses — Classic gotcha
 * #43's "audio volumes are master buses; new sound code connects to the
 * bus, never AC.destination directly," ported to 3D. Lazily created on the
 * first real user gesture (browsers refuse to start an AudioContext before
 * one — this game already requires a pointerdown/keydown to do anything,
 * so `unlock()` is wired to those same first events in main.ts) and
 * suspended/resumed in lockstep with tab visibility, mirroring GameLoop's
 * own visibilitychange handling: without this, a backgrounded tab would
 * keep emitting audio into a screen nobody's looking at (battery drain,
 * sound bleeding over whatever app the player switched to) even though
 * GameLoop itself has already stopped ticking.
 *
 * `pausedByMenu` is tracked separately from the raw AudioContext state so
 * the visibilitychange handler and the pause-menu's own suspend/resume
 * don't fight each other — e.g. backgrounding the tab WHILE the pause menu
 * is open must not have the visibilitychange handler resume audio the
 * instant the tab returns to foreground; only an explicit `resumeFromPause`
 * (the RESUME button) may clear that flag.
 */
export class AudioBuses {
  private ctx: AudioContext | null = null;
  private music: GainNode | null = null;
  private sfx: GainNode | null = null;
  private musicVolume = 1;
  private sfxVolume = 1;
  private unlocked = false;
  private pausedByMenu = false;

  /** Test/verification seam — the harness reads this after triggering a gameplay action instead of inspecting live WebAudio node graphs, matching this project's existing "spy on the actual call, don't reimplement it" convention. Set unconditionally by `audio/sfx.ts`'s `playSfx`, including before the audio context exists (so a check run before the first user gesture still observes intent). */
  lastSfxPlayed: { name: string; positional: boolean } | null = null;

  constructor() {
    document.addEventListener('visibilitychange', () => {
      if (!this.ctx || !this.unlocked) return;
      if (document.hidden) void this.ctx.suspend();
      else if (!this.pausedByMenu) void this.ctx.resume();
    });
  }

  /** Call from a real user-gesture handler (pointerdown/keydown) — idempotent, safe to call from more than one listener racing to be "the first" gesture. */
  unlock(): void {
    if (this.unlocked) return;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return; // no WebAudio support at all — sfx/music calls stay silent no-ops (see sfx.ts/music.ts's own `!ctx` guards)
    this.ctx = new Ctor();
    this.music = this.ctx.createGain();
    this.music.gain.value = this.musicVolume;
    this.music.connect(this.ctx.destination);
    this.sfx = this.ctx.createGain();
    this.sfx.gain.value = this.sfxVolume;
    this.sfx.connect(this.ctx.destination);
    this.unlocked = true;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  get musicBus(): GainNode | null {
    return this.music;
  }

  get sfxBus(): GainNode | null {
    return this.sfx;
  }

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  /** Volumes are stored regardless of unlock state, so a setting changed before the first gesture is still correctly applied the moment `unlock()` actually builds the gain nodes. */
  setMusicVolume(v: number): void {
    this.musicVolume = v;
    if (this.music) this.music.gain.value = v;
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = v;
    if (this.sfx) this.sfx.gain.value = v;
  }

  /** Everything (music + any in-flight sfx envelope) freezes atomically — exactly what "paused" should mean, and far simpler than pausing each node individually. */
  suspendForPause(): void {
    this.pausedByMenu = true;
    if (this.ctx && this.ctx.state === 'running') void this.ctx.suspend();
  }

  resumeFromPause(): void {
    this.pausedByMenu = false;
    // The `!document.hidden` guard matters if RESUME is somehow reached
    // while backgrounded (not reachable via a real click on a hidden tab,
    // but cheap to guard rather than assume) — the visibilitychange
    // handler will correctly resume on its own once the tab is foregrounded.
    if (this.ctx && this.ctx.state === 'suspended' && !document.hidden) void this.ctx.resume();
  }
}
